import { useCallback, useEffect, useState } from 'react'

import { FileText, Plus } from '@gravity-ui/icons'
import { Button, EmptyState, Spinner, Table } from '@heroui/react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { Card, CardContent } from '@/components/ui/Card'
import { ConfirmDeleteDialog } from '@/components/ui/ConfirmDeleteDialog'
import { DateInput } from '@/components/ui/DateInput'
import { PageHeader } from '@/components/ui/PageHeader'
import { usePaymentsStore } from '@/store/payments.store'
import type { PaidReport, SettlementItem } from '@/types/settlement'
import { formatPLN } from '@/utils/currency'

import PaymentDialog from '../components/PaymentDialog'

import { InfoCard } from './components/InfoCard'
import { InitialBalanceRow } from './components/InitialBalanceRow'
import { PaymentRow } from './components/PaymentRow'
import { ReportRow } from './components/ReportRow'

type TimelineItem =
  | {
    type: 'payment';
    id: string;
    date: string;
    amount: number;
    paidReports?: PaidReport[];
  }
  | {
    type: 'report';
    id: string;
    date: string;
    dateFrom: string;
    dateTo: string;
    amount: number;
    identifier: string;
  }
  | {
    type: 'opening_balance';
    id: string;
    date: string;
    amount: number;
  };

export default function SettlementDetail() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()

  const {
    currentSettlement,
    isLoading,
    fetchDriverSettlement,
    deletePayment,
  } = usePaymentsStore()

  const [dateFilter, setDateFilter] = useState<string>('')
  const [isPaymentOpen, setIsPaymentOpen] = useState(false)
  const [dialogIsInitialBalance, setDialogIsInitialBalance] = useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | undefined>(undefined)
  const [deletingPaymentId, setDeletingPaymentId] = useState<string | undefined>(undefined)
  const [reloadTrigger, setReloadTrigger] = useState(0)

  useEffect(() => {
    if (id) {
      fetchDriverSettlement(id, dateFilter || undefined)
    }
  }, [id, dateFilter, fetchDriverSettlement, reloadTrigger])

  const triggerReload = useCallback(() => {
    setReloadTrigger(prev => prev + 1)
  }, [])

  const handlePaymentSuccess = useCallback(() => {
    triggerReload()
  }, [triggerReload])

  const handleBack = useCallback(() => {
    navigate('/settlements')
  }, [navigate])

  const handleOpenPayment = useCallback(() => {
    setSelectedPaymentId(undefined)
    setDialogIsInitialBalance(false)
    setIsPaymentOpen(true)
  }, [])

  const handleOpenInitialBalance = useCallback(() => {
    setSelectedPaymentId(undefined)
    setDialogIsInitialBalance(true)
    setIsPaymentOpen(true)
  }, [])

  const handleEditPayment = useCallback((paymentId: string) => {
    const item = currentSettlement?.Items.find(i => i.ID === paymentId)
    setSelectedPaymentId(paymentId)
    setDialogIsInitialBalance(item?.Type === 'opening_balance')
    setIsPaymentOpen(true)
  }, [currentSettlement])

  const handleDeletePayment = useCallback((paymentId: string) => {
    setDeletingPaymentId(paymentId)
    setIsDeleteOpen(true)
  }, [])

  const handleDeleteConfirm = useCallback(async () => {
    if (deletingPaymentId && id) {
      try {
        const item = currentSettlement?.Items.find(i => i.ID === deletingPaymentId)
        if (item?.Type === 'opening_balance') {
          const { deleteInitialBalance } = usePaymentsStore.getState()
          await deleteInitialBalance(id, deletingPaymentId)
        } else {
          await deletePayment(id, deletingPaymentId)
        }
        handlePaymentSuccess()
      } catch (err) {
        console.error('Failed to delete transaction:', err)
      } finally {
        setIsDeleteOpen(false)
        setDeletingPaymentId(undefined)
      }
    }
  }, [id, deletingPaymentId, deletePayment, handlePaymentSuccess, currentSettlement])

  const handleNavigateToReport = useCallback((reportId: string) => {
    navigate(`/reports/${reportId}`)
  }, [navigate])

  const renderEmptyState = useCallback(() => (
    <EmptyState className="flex h-30 mt-6 w-full flex-col items-center justify-center gap-4 text-center">
      <FileText className="size-6 text-zinc-400" />
      <span className="text-sm text-zinc-500">{t('common.no_results', 'No results found')}</span>
    </EmptyState>
  ), [t])

  if (isLoading && !currentSettlement) {
    return (
      <div className="flex justify-center py-20">
        <Spinner size="lg" />
      </div>
    )
  }

  const driver = currentSettlement?.Driver

  if (!driver) {
    return (
      <div className="p-8 text-center text-zinc-400">
        <p className="mb-4">{t('settlements.driver_not_found')}</p>
        <Button variant="ghost" onClick={handleBack}>
          {t('common.back')}
        </Button>
      </div>
    )
  }

  const balance = currentSettlement?.Total ?? 0

  // Build timeline items from backend and local storage data
  const rawTimelineItems: TimelineItem[] = (currentSettlement?.Items ?? []).map((item: SettlementItem) => {
    if (item.Type === 'payment') {
      return {
        type: 'payment' as const,
        id: item.ID,
        date: item.Date,
        amount: item.Amount,
        paidReports: item.PaidReports,
      }
    } else if (item.Type === 'opening_balance') {
      return {
        type: 'opening_balance' as const,
        id: item.ID,
        date: item.StartDate,
        amount: item.Total,
      }
    } else {
      return {
        type: 'report' as const,
        id: item.ID,
        date: item.EndDate,
        dateFrom: item.StartDate,
        dateTo: item.EndDate,
        amount: item.Total,
        identifier: item.Name,
      }
    }
  })

  const timelineItems = [...rawTimelineItems].sort((a, b) => a.date.localeCompare(b.date))

  const hasOpeningBalance = currentSettlement?.Items.some(item => item.Type === 'opening_balance')

  return (
    <div className="p-5 space-y-6">
      <PageHeader
        onBack={handleBack}
        title={`${t('settlements.title')} — ${driver.Name}`}
        subtitle={t('settlements.driver')}
        actions={
          <div className="flex gap-2">
            {!hasOpeningBalance && (
              <Button onClick={handleOpenInitialBalance}>
                <Plus />
                {t('settlements.add_initial_balance')}
              </Button>
            )}
            <Button onClick={handleOpenPayment}>
              <Plus />
              {t('settlements.add_payment')}
            </Button>
          </div>
        }
      />

      {/* Info Grid */}
      <div className="grid grid-cols-3 gap-4">
        <InfoCard
          label={t('settlements.driver')}
          value={driver.Name}
          subValue={driver.Phone ?? '—'}
        />
        <InfoCard
          label={t('settlements.vehicle')}
          value={driver.Vehicle?.Number ?? '—'}
          subValue={driver.Vehicle?.Model ?? '—'}
        />
        <InfoCard
          label={t('settlements.summary_title')}
          value={`${formatPLN(balance)} PLN`}
          subValue={
            balance > 0
              ? t('settlements.left_amount')
              : balance < 0
                ? t('settlements.overspent_amount')
                : t('settlements.balance_cleared')
          }
        />
      </div>

      <Card>
        <CardContent className="p-2">
          <div className="flex gap-4 items-center flex-wrap">
            <div className="w-full sm:w-128">
              <DateInput
                value={dateFilter}
                onChange={setDateFilter}
                isClearable
                variant="secondary"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        <h2 className="text-lg font-bold text-zinc-800 dark:text-zinc-100">
          {t('settlements.history')}
        </h2>

        {isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner size="md" />
          </div>
        ) : (
          <Table className="min-h-[200px]">
            <Table.ScrollContainer>
              <Table.Content aria-label="Settlement transactions table" className="min-w-[600px]">
                <Table.Header>
                  <Table.Column width={40}></Table.Column>
                  <Table.Column>{t('common.date')}</Table.Column>
                  <Table.Column>{t('settlements.operation')}</Table.Column>
                  <Table.Column className="text-right">{t('reports.table.amount')}</Table.Column>
                  <Table.Column width={80}></Table.Column>
                </Table.Header>
                <Table.Body renderEmptyState={renderEmptyState}>
                  {timelineItems.map(item => {
                    if (item.type === 'payment') {
                      return (
                        <PaymentRow
                          key={item.id}
                          id={item.id}
                          date={item.date}
                          amount={item.amount}
                          paidReports={item.paidReports}
                          onEdit={handleEditPayment}
                          onDelete={handleDeletePayment}
                        />
                      )
                    } else if (item.type === 'opening_balance') {
                      return (
                        <InitialBalanceRow
                          key={item.id}
                          id={item.id}
                          startDate={item.date}
                          total={item.amount}
                          onEdit={handleEditPayment}
                          onDelete={handleDeletePayment}
                        />
                      )
                    } else {
                      return (
                        <ReportRow
                          key={item.id}
                          id={item.id}
                          dateFrom={item.dateFrom}
                          dateTo={item.dateTo}
                          amount={item.amount}
                          identifier={item.identifier}
                          onNavigate={handleNavigateToReport}
                        />
                      )
                    }
                  })}
                </Table.Body>
              </Table.Content>
            </Table.ScrollContainer>
            <Table.Footer>
              <div className="flex w-full gap-6 py-2 justify-end">
                <div className="text-right">
                  <div className="text-xs font-black text-zinc-600 dark:text-zinc-400">
                    {t('settlements.left_amount')}
                  </div>
                  <div className="text-md font-bold text-emerald-600 dark:text-emerald-400 mt-1">
                    {balance > 0 ? `${formatPLN(balance)} PLN` : '0.00 PLN'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs font-black text-zinc-600 dark:text-zinc-400">
                    {t('settlements.overspent_amount')}
                  </div>
                  <div className="text-md font-bold text-rose-600 dark:text-rose-400 mt-1">
                    {balance < 0 ? `${formatPLN(Math.abs(balance))} PLN` : '0.00 PLN'}
                  </div>
                </div>
              </div>
            </Table.Footer>
          </Table>
        )}
      </div>

      <PaymentDialog
        isOpen={isPaymentOpen}
        onOpenChange={setIsPaymentOpen}
        driverId={driver.ID}
        paymentId={selectedPaymentId}
        onSuccess={handlePaymentSuccess}
        isInitialBalance={dialogIsInitialBalance}
      />

      <ConfirmDeleteDialog
        isOpen={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        onConfirm={handleDeleteConfirm}
        title={t('common.delete')}
        description={
          currentSettlement?.Items.find(item => item.ID === deletingPaymentId)?.Type === 'opening_balance'
            ? t('settlements.confirm_delete_initial_balance')
            : t('settlements.confirm_delete_payment')
        }
        isLoading={isLoading}
      />
    </div>
  )
}
